import { altLevelLabel, levelLabel } from '../IndividualPlan/derive';
import type { ProfileStanding } from './studentProfileApi';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOMAIN_ORDER = ['listening', 'speaking', 'reading', 'writing'] as const;
const DOMAIN_NAMES: Record<(typeof DOMAIN_ORDER)[number], string> = { listening: 'Listening', speaking: 'Speaking', reading: 'Reading', writing: 'Writing' };
const NOT_IN_FILE = 'not in your file';

export function levelName(level: number | null, alternate: boolean): string {
  if (level == null) return 'not scored';
  if (alternate) return `Level ${Math.floor(level)} ${altLevelLabel(level)}`;
  return levelLabel(level);
}

export function domainExtremes(domains: Record<string, number>): { highest: string; lowest: string } | null {
  const scored = DOMAIN_ORDER.filter((key) => domains[key] != null);
  if (scored.length === 0) return null;
  const pick = (better: (a: number, b: number) => boolean) =>
    scored.reduce((best, key) => (better(domains[key] as number, domains[best] as number) ? key : best));
  return { highest: DOMAIN_NAMES[pick((a, b) => a > b)], lowest: DOMAIN_NAMES[pick((a, b) => a < b)] };
}

export function distanceToExit(level: number, bar: number): string {
  return level >= bar ? 'met' : `${(bar - level).toFixed(1)} below`;
}

export function profileYear(endYear: number): string {
  return `${endYear - 1}–${String(endYear).slice(2)}`;
}

export function displayYear(label: string): string {
  return label.replace('-', '–');
}

export function exitDate(exitYearEnd: number, exitMonth: number): string {
  if (!exitMonth) return profileYear(exitYearEnd);
  return monthYear(exitMonth, exitMonth >= 7 ? exitYearEnd - 1 : exitYearEnd);
}

export function monthYear(month: number, year: number): string {
  const name = MONTHS[month - 1];
  return name ? `${name} ${year}` : `${year}`;
}

export function longDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

export function lastSeenLine(lastSeenYearEnd: number): string {
  return `No longer enrolled — last appeared in the ${profileYear(lastSeenYearEnd)} data. Showing last-known information.`;
}

export function monitoringWindow(monitoringYears: number, settingKnown: boolean): string {
  return settingKnown ? `the district’s setting is ${monitoringYears} years` : `the usual monitoring window is ${monitoringYears} years (the district’s setting could not be read)`;
}

export function monitoringLine({ exitYearEnd, exitMonth, currentYearEnd, monitoringYears, settingKnown = true }: {
  exitYearEnd: number;
  exitMonth: number;
  currentYearEnd: number;
  monitoringYears: number;
  settingKnown?: boolean;
}): string | null {
  if (!exitYearEnd) return null;
  const years = Math.max(1, currentYearEnd - exitYearEnd);
  if (years <= monitoringYears) {
    const source = settingKnown ? 'from the district’s monitoring setting' : 'from the usual monitoring window (the district’s setting could not be read)';
    return `In monitoring since ${exitDate(exitYearEnd, exitMonth)} · year ${years} of ${monitoringYears}, ${source}`;
  }
  return `The SIS still lists Monitoring, ${years} years after exit; ${monitoringWindow(monitoringYears, settingKnown)}.`;
}

function schoolYearOrdinal(month: number): number {
  return month >= 7 ? month - 12 : month;
}

export function sittingAfterExit(sitting: { schoolYearEnd: number; month?: number }, exitYearEnd: number, exitMonth: number): boolean {
  if (sitting.schoolYearEnd !== exitYearEnd) return sitting.schoolYearEnd > exitYearEnd;
  if (!sitting.month || !exitMonth) return false;
  return schoolYearOrdinal(sitting.month) > schoolYearOrdinal(exitMonth);
}

export const PLANS_STATUS_UNREAD = 'The plans status could not be read. Refresh the page to try again.';

export function noPlanReason({ inFile, noScores, plansGenerated, plansGeneratedKnown = true, year, teacher }: {
  inFile: boolean;
  noScores: boolean;
  plansGenerated: boolean;
  plansGeneratedKnown?: boolean;
  year: string;
  teacher: boolean;
}): string | null {
  if (!inFile || noScores) return 'No ACCESS scores on file, so a plan can’t be generated.';
  if (!plansGenerated && !plansGeneratedKnown) return PLANS_STATUS_UNREAD;
  const shown = displayYear(year);
  if (!plansGenerated) return teacher ? `Your EL office hasn’t generated ${shown} plans yet.` : `Plans for ${shown} haven’t been generated yet.`;
  return null;
}

export function currentPlanOf(profile: { plans: { studentId: string; schoolYear: string; generatedAt: string }[]; currentSchoolYear: string }) {
  return profile.plans.find((plan) => plan.schoolYear === profile.currentSchoolYear) ?? null;
}

type BylineFields = { grade: string; school: string; elTeachers: string[] };

export function bylineOf({ grade, school, elTeachers }: BylineFields, standing: ProfileStanding): string {
  const gradeText = /^(k|kg|kindergarten|0)$/i.test(grade.trim()) ? 'Kindergarten' : grade.trim() ? `Grade ${grade.trim()}` : `Grade ${NOT_IN_FILE}`;
  const schoolText = school.trim() ? school.trim().replace(/ School$/, '') : `School ${NOT_IN_FILE}`;
  const teacherText = standing !== 'departed' && elTeachers.length ? `EL teacher ${elTeachers.join(', ')}` : `EL teacher ${NOT_IN_FILE}`;
  return [gradeText, schoolText, teacherText].join(' · ');
}
